/**
 * Offline conflict store (Phase 1: record + list; no full resolve UI).
 * @see docs/OFFLINE_FIRST_DESIGN.md §1.4
 */

import { get, set, del } from "idb-keyval"
import type { ConflictReason, ConflictRecord, EntityType } from "./types"

export const CONFLICTS_KEY = "offline:conflicts"

const MAX_CONFLICTS = 100

async function readAll(): Promise<ConflictRecord[]> {
  const rows = await get<ConflictRecord[]>(CONFLICTS_KEY)
  return rows ?? []
}

async function writeAll(rows: ConflictRecord[]): Promise<void> {
  await set(CONFLICTS_KEY, rows.slice(-MAX_CONFLICTS))
}

function newConflictId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `cfl-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export async function recordConflict(input: {
  opId: string
  entityType: EntityType
  entityId: string
  reason: ConflictReason
  clientPayload: Record<string, unknown>
  serverSnapshot?: Record<string, unknown> | null
  httpStatus?: number | null
  errorMessage?: string | null
}): Promise<ConflictRecord> {
  const row: ConflictRecord = {
    conflictId: newConflictId(),
    opId: input.opId,
    entityType: input.entityType,
    entityId: input.entityId,
    reason: input.reason,
    clientPayload: input.clientPayload,
    serverSnapshot: input.serverSnapshot ?? null,
    httpStatus: input.httpStatus ?? null,
    errorMessage: input.errorMessage ?? null,
    createdAt: Date.now(),
    status: "open",
  }
  const all = await readAll()
  all.push(row)
  await writeAll(all)
  return row
}

export async function listConflicts(status: ConflictRecord["status"] | "all" = "open"): Promise<ConflictRecord[]> {
  const all = await readAll()
  if (status === "all") return all
  return all.filter((c) => c.status === status)
}

export async function getOpenConflictCount(): Promise<number> {
  const open = await listConflicts("open")
  return open.length
}

export async function dismissConflict(conflictId: string): Promise<void> {
  const all = await readAll()
  const next = all.map((c) =>
    c.conflictId === conflictId ? { ...c, status: "dismissed" as const } : c,
  )
  await writeAll(next)
}

export async function clearConflicts(): Promise<void> {
  await del(CONFLICTS_KEY)
}

export function reasonFromHttpStatus(status: number): ConflictReason {
  if (status === 409) return "version_mismatch"
  if (status === 401 || status === 403) return "permission"
  return "unknown"
}
